import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatDateTime } from '@/lib/format';

import { DispatchStatusBadge } from './DispatchStatusBadge';
import { formatDispatchTime } from './format';
import type { DispatchRequestDto, DispatchStop } from './types';

const NS = 'operations';
const EMPTY = '—';

interface DispatchRequestPreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DispatchRequestDto | null;
  supplierName?: string;
  facilityName?: string;
}

const present = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY;
};

const presentDateTime = (value?: string | null) => (value ? formatDateTime(value) : EMPTY);

const displayStops = (request: DispatchRequestDto): DispatchStop[] => {
  const stops = (request.stops ?? [])
    .filter((stop) => stop.location?.trim())
    .sort((a, b) => a.stop_seq - b.stop_seq);

  if (stops.length > 0) return stops;
  return request.location?.trim() ? [{ stop_seq: 1, location: request.location, notes: null }] : [];
};

const DetailField = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-1 rounded-md border border-efs-border bg-efs-bg-subtle p-3">
    <dt className="text-xs font-medium text-efs-text-muted">{label}</dt>
    <dd className="break-words text-sm text-efs-text-primary">{children}</dd>
  </div>
);

/**
 * Read-only dispatch request detail surface shared by supplier, platform and
 * facility lists. It intentionally consumes the existing row DTO only; opening
 * the preview never fetches or mutates backend state.
 */
export const DispatchRequestPreviewSheet = ({
  open,
  onOpenChange,
  request,
  supplierName,
  facilityName,
}: DispatchRequestPreviewSheetProps) => {
  const { t } = useTranslation(NS);

  if (!request) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" />
      </Sheet>
    );
  }

  const stops = displayStops(request);
  const fallbackSupplier = `${request.supplier_company_id.slice(0, 8)}…`;
  const fallbackFacility = request.routed_facility_id
    ? `${request.routed_facility_id.slice(0, 8)}…`
    : EMPTY;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            {t('efs.operations.dispatch.preview.title', {
              defaultValue: '配車・配送依頼詳細',
            })}
            <span className="ml-2 font-mono text-sm text-efs-text-muted">
              {request.trip_code ?? request.temporary_code}
            </span>
          </SheetTitle>
          <SheetDescription>
            {t('efs.operations.dispatch.preview.description', {
              defaultValue: '一覧データから配車・配送依頼の詳細を確認します。',
            })}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section aria-labelledby="dispatch-preview-summary" className="space-y-3">
            <h3 id="dispatch-preview-summary" className="text-sm font-semibold">
              {t('efs.operations.dispatch.preview.sections.summary', {
                defaultValue: '基本情報',
              })}
            </h3>
            <dl className="grid gap-3 sm:grid-cols-2">
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.dispatch_request_id', {
                  defaultValue: '配車依頼 ID',
                })}
              >
                <span className="font-mono text-xs">{request.dispatch_request_id}</span>
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.trip_code')}>
                <span className="font-mono">{request.trip_code ?? '—'}</span>
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.status')}>
                <div className="flex flex-wrap items-center gap-2">
                  <DispatchStatusBadge status={request.status} />
                  <span>{t(`efs.operations.dispatch.statuses.${request.status}`)}</span>
                </div>
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.type')}>
                {t(`efs.operations.dispatch.types.${request.request_type}`, {
                  defaultValue: request.request_type,
                })}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.supplier')}>
                {supplierName ?? fallbackSupplier}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.facility')}>
                {facilityName ?? fallbackFacility}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.requested_date')}>
                {present(request.requested_date)}
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.fields.requested_time', {
                  defaultValue: '希望時間',
                })}
              >
                {formatDispatchTime(request.requested_time)}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.vehicle_number')}>
                {present(request.vehicle_number)}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.receivable_date')}>
                {present(request.receivable_date)}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.cargo_description')}>
                {present(request.cargo_description)}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.cargo_quantity')}>
                {present(request.cargo_quantity)}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.contact_name')}>
                {present(request.contact_name)}
              </DetailField>
              <DetailField label={t('efs.operations.dispatch.fields.contact_phone')}>
                {present(request.contact_phone)}
              </DetailField>
            </dl>
          </section>

          <section aria-labelledby="dispatch-preview-stops" className="space-y-3">
            <h3 id="dispatch-preview-stops" className="text-sm font-semibold">
              {t('efs.operations.dispatch.preview.sections.stops', {
                defaultValue: '引取場所',
              })}
            </h3>
            {stops.length > 0 ? (
              <ol className="space-y-2">
                {stops.map((stop, index) => (
                  <li
                    key={`${stop.stop_seq}-${stop.location}`}
                    className="rounded-md border border-efs-border p-3"
                  >
                    <p className="text-sm font-medium">
                      {t('efs.operations.dispatch.fields.stop_seq', {
                        seq: stop.stop_seq || index + 1,
                        defaultValue: '引取場所 {{seq}}',
                      })}
                    </p>
                    <p className="mt-1 text-sm">{present(stop.location)}</p>
                    <p className="mt-2 whitespace-pre-wrap text-xs text-efs-text-muted">
                      {t('efs.operations.dispatch.preview.stop_notes_label', {
                        defaultValue: '場所メモ',
                      })}
                      : {present(stop.notes)}
                    </p>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="rounded-md border border-dashed border-efs-border p-3 text-sm text-efs-text-muted">
                {EMPTY}
              </p>
            )}
          </section>

          <section aria-labelledby="dispatch-preview-notes" className="space-y-3">
            <h3 id="dispatch-preview-notes" className="text-sm font-semibold">
              {t('efs.operations.dispatch.preview.sections.notes', {
                defaultValue: '備考',
              })}
            </h3>
            <dl className="grid gap-3">
              <DetailField label={t('efs.operations.dispatch.fields.notes')}>
                <span className="whitespace-pre-wrap">{present(request.notes)}</span>
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.rejection_reason', {
                  defaultValue: '却下理由',
                })}
              >
                <span className="whitespace-pre-wrap">{present(request.rejection_reason)}</span>
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.return_suggested_availability', {
                  defaultValue: '再配車可能日時',
                })}
              >
                {request.return_suggested_availability?.length
                  ? request.return_suggested_availability
                      .map((slot) => `${slot.date} ${slot.start_time.slice(0, 5)}`)
                      .join(' / ')
                  : EMPTY}
              </DetailField>
            </dl>
          </section>

          <section aria-labelledby="dispatch-preview-links" className="space-y-3">
            <h3 id="dispatch-preview-links" className="text-sm font-semibold">
              {t('efs.operations.dispatch.preview.sections.links', {
                defaultValue: 'リンク情報',
              })}
            </h3>
            <dl className="grid gap-3 sm:grid-cols-2">
              <DetailField label={t('efs.operations.dispatch.fields.monthly_batch')}>
                <span className="font-mono text-xs">{present(request.monthly_batch_id)}</span>
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.linked_inbound_lot', {
                  defaultValue: '入荷ロット ID',
                })}
              >
                <span className="font-mono text-xs">{present(request.linked_inbound_lot_id)}</span>
              </DetailField>
            </dl>
          </section>

          <section aria-labelledby="dispatch-preview-timestamps" className="space-y-3">
            <h3 id="dispatch-preview-timestamps" className="text-sm font-semibold">
              {t('efs.operations.dispatch.preview.sections.timestamps', {
                defaultValue: '日時',
              })}
            </h3>
            <dl className="grid gap-3 sm:grid-cols-2">
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.created_at', {
                  defaultValue: '作成日時',
                })}
              >
                {presentDateTime(request.created_at)}
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.submitted_at', {
                  defaultValue: '送信日時',
                })}
              >
                {presentDateTime(request.submitted_at)}
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.routed_at', {
                  defaultValue: '割当日時',
                })}
              >
                {presentDateTime(request.routed_at)}
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.routed_by', {
                  defaultValue: '割当者',
                })}
              >
                {present(request.routed_by)}
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.accepted_at', {
                  defaultValue: '受入日時',
                })}
              >
                {presentDateTime(request.accepted_at)}
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.accepted_by', {
                  defaultValue: '受入担当者',
                })}
              >
                {present(request.accepted_by)}
              </DetailField>
              <DetailField
                label={t('efs.operations.dispatch.preview.fields.updated_at', {
                  defaultValue: '更新日時',
                })}
              >
                {presentDateTime(request.updated_at)}
              </DetailField>
            </dl>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DispatchRequestPreviewSheet;
